import React, { useEffect, createElement, Component } from 'react';
import { CheckCircle2, XCircle, Activity, Brain, Battery, ShieldCheck, ArrowRight, Lock, Zap, Moon, Flame } from 'lucide-react';
import { FAQAccordion } from '../components/FAQAccordion';
import { LegalFooter } from '../components/LegalFooter';
declare global {
  interface Window {
    fbq: (...args: unknown[]) => void;
  }
}
function initMetaPixel(pixelId: string) {
  if (typeof window.fbq === 'function') return;
  const n: any = window.fbq = function (...args: unknown[]) {
    n.callMethod ? n.callMethod.apply(n, args) : n.queue.push(args);
  };
  n.push = n;
  n.loaded = true;
  n.version = '2.0';
  n.queue = [];
  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://connect.facebook.net/en_US/fbevents.js';
  document.head.appendChild(script);
  window.fbq('init', pixelId);
}
const PRODUCT_IMAGE = 'https://cdn.magicpatterns.com/uploads/9NSQJW7yK3PDGXHGHZUew2/Protocolo_adios_cortisol.png';
// Reusable CTA Button Component
const CTAButton = ({
  className = '',
  scrollTo
}: {
  className?: string;
  scrollTo?: string;
}) => <a href={scrollTo || 'https://pay.hotmart.com/K104853845N'} target={scrollTo ? undefined : '_blank'} rel={scrollTo ? undefined : 'noopener noreferrer'} onClick={e => {
  if (scrollTo) {
    e.preventDefault();
    document.querySelector(scrollTo)?.scrollIntoView({
      behavior: 'smooth'
    });
  } else {
    window.fbq('track', 'AddToCart', {
      content_name: 'Protocolo Adiós Cortisol',
      content_type: 'product',
      value: 6.39,
      currency: 'USD'
    });
  }
}} className={`group relative inline-flex items-center justify-center gap-3 px-8 py-5 bg-amber-500 hover:bg-amber-400 text-slate-900 font-extrabold text-lg md:text-xl rounded-xl transition-all duration-300 shadow-[0_0_20px_rgba(245,158,11,0.4)] hover:shadow-[0_0_30px_rgba(245,158,11,0.6)] hover:-translate-y-1 w-full md:w-auto text-center ${className}`} data-id="element-50">
    <span data-id="element-51">QUIERO EL PROTOCOLO ADIÓS CORTISOL AHORA</span>
    <ArrowRight className="w-6 h-6 group-hover:translate-x-1 transition-transform" data-id="element-52" />
  </a>;
export function SalesPage() {
  useEffect(() => {
    initMetaPixel('1283742087232286');
    window.fbq('track', 'PageView');
  }, []);
  return <div className="min-h-screen bg-[#0B1120] text-[#E8E8E8] font-sans selection:bg-teal-500/30" data-id="element-53">
      {/* 1. HERO SECTION */}
      <section className="relative pt-20 pb-16 md:pt-32 md:pb-24 overflow-hidden border-b border-slate-800/50" data-id="element-54">
        {/* Bio-tech background glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-teal-900/20 blur-[120px] rounded-full pointer-events-none" data-id="element-55"></div>

        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10" data-id="element-56">
          <div className="grid md:grid-cols-2 gap-12 items-center" data-id="element-57">
            <div className="space-y-8 text-center md:text-left" data-id="element-58">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-500/10 border border-teal-500/20 text-teal-400 text-sm font-semibold tracking-wide uppercase" data-id="element-59">
                <Activity className="w-4 h-4" data-id="element-60" />
                <span data-id="element-61">Regulación del Sistema Nervioso</span>
              </div>

              <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold text-white leading-tight tracking-tight" data-id="element-62">
                El Interruptor Maestro para{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-cyan-300" data-id="element-63">
                  Desinflamar tu Cuerpo
                </span>{' '}
                en 14 Días
              </h1>

              <p className="text-xl md:text-2xl text-slate-300 leading-relaxed font-medium" data-id="element-64">
                Recupera tu energía vital y elimina la "panza por estrés"
                regulando tu sistema nervioso, sin dietas imposibles ni horas de
                cardio.
              </p>

              <div className="pt-4 flex flex-col items-center md:items-start gap-4" data-id="element-65">
                <CTAButton scrollTo="#checkout" data-id="element-66" />
                <div className="flex items-center gap-2 text-sm text-slate-400" data-id="element-67">
                  <Lock className="w-4 h-4" data-id="element-68" />
                  <span data-id="element-69">Acceso inmediato y seguro. Garantía de 7 días.</span>
                </div>
              </div>
            </div>

            <div className="relative mx-auto w-full max-w-md md:max-w-full" data-id="element-70">
              <div className="absolute inset-0 bg-gradient-to-tr from-teal-500/20 to-amber-500/20 blur-3xl rounded-3xl" data-id="element-71"></div>
              <img src={PRODUCT_IMAGE} alt="Protocolo Adiós Cortisol en iPad" className="relative z-10 w-full h-auto drop-shadow-2xl rounded-2xl border border-slate-700/50" data-id="element-72" />
            </div>
          </div>
        </div>
      </section>

      {/* 2. OPENING HOOK & PROBLEM */}
      <section className="py-20 bg-[#0F172A]" data-id="element-73">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12" data-id="element-74">
          <div className="prose prose-lg prose-invert mx-auto" data-id="element-75">
            <p className="text-xl leading-relaxed text-slate-300" data-id="element-76">
              ¿Te has despertado alguna vez sintiendo que ya estás cansado,
              antes de que tus pies toquen el suelo? Miras el reloj, son las
              7:00 AM, y aunque "dormiste", tu cuerpo pesa. Te miras al espejo y
              notas esa inflamación en el rostro y un abdomen abultado que ayer
              no estaba tan marcado.
            </p>
            <p className="text-xl leading-relaxed text-slate-300 font-semibold text-white mt-6" data-id="element-77">
              No es falta de voluntad. No es que estés comiendo "mal". Es que tu
              cuerpo está atrapado en un ciclo de supervivencia biológica del
              que no sabe cómo salir.
            </p>
          </div>

          <div className="bg-[#111827] border border-slate-800 rounded-2xl p-8 md:p-10 shadow-xl relative overflow-hidden" data-id="element-78">
            <div className="absolute top-0 right-0 w-32 h-32 bg-red-500/5 blur-[50px] rounded-full" data-id="element-79"></div>
            <h2 className="text-3xl font-bold text-white mb-6 flex items-center gap-3" data-id="element-80">
              <Flame className="w-8 h-8 text-amber-500" data-id="element-81" />
              El Secuestro Metabólico
            </h2>
            <p className="text-lg leading-relaxed mb-6" data-id="element-82">
              Vives en un estado de "alerta roja" constante. Tu cerebro percibe
              el tráfico, los correos electrónicos y las deudas como una amenaza
              de muerte inminente. Como respuesta, tus glándulas adrenales
              inundan tu sangre con cortisol.
            </p>
            <p className="text-lg leading-relaxed font-medium text-amber-100/80" data-id="element-83">
              El cortisol crónico secuestra tu metabolismo. Da la orden de
              almacenar grasa específicamente en tu abdomen para "proteger tus
              órganos", apaga tu digestión causándote gases y destruye tu ciclo
              de sueño. Estás biológicamente bloqueado para perder peso, sin
              importar cuánto te esfuerces.
            </p>
          </div>
        </div>
      </section>

      {/* 3. WHY WHAT YOU TRIED DIDN'T WORK */}
      <section className="py-20 bg-[#0B1120] border-y border-slate-800/50" data-id="element-84">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-85">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-12" data-id="element-86">
            Por qué lo que has intentado{' '}
            <span className="text-red-400" data-id="element-87">te está haciendo daño</span>
          </h2>

          <div className="grid md:grid-cols-2 gap-6" data-id="element-88">
            {['El cardio intenso y el HIIT disparan más cortisol, inflamándote aún más en lugar de quemar grasa.', 'Las dietas restrictivas o el ayuno prolongado estresan a un sistema nervioso que ya se siente en peligro.', "El café en ayunas crea picos de energía artificiales que terminan en un 'crash' devastador a las 4 PM.", 'Los suplementos de melatonina solo sedan tu cerebro, pero no reparan tu ritmo circadiano profundo.'].map((item, i) => <div key={i} className="flex items-start gap-4 bg-[#0F172A] p-6 rounded-xl border border-slate-800" data-id="element-89">
                <XCircle className="w-6 h-6 text-red-500 flex-shrink-0 mt-1" data-id="element-90" />
                <p className="text-lg leading-relaxed text-slate-300" data-id="element-91">{item}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 4. THE UNIQUE MECHANISM */}
      <section className="py-24 bg-[#111827] relative overflow-hidden" data-id="element-92">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-teal-900/10 blur-[100px] rounded-full pointer-events-none" data-id="element-93"></div>

        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-94">
          <Brain className="w-16 h-16 text-teal-400 mx-auto mb-6" data-id="element-95" />
          <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-8" data-id="element-96">
            La Solución Biológica: <br data-id="element-97" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-400 to-cyan-300" data-id="element-98">
              La Resonancia Vagal
            </span>
          </h2>

          <p className="text-xl leading-relaxed text-slate-300 mb-8 text-left md:text-center" data-id="element-99">
            Este protocolo no se basa en el déficit calórico, sino en la{' '}
            <strong data-id="element-100">seguridad biológica</strong>. A través de la estimulación
            dirigida del Nervio Vago, enviamos una señal directa a tu tronco
            encefálico para informarle que el peligro ha pasado.
          </p>

          <div className="bg-teal-950/30 border border-teal-500/20 rounded-2xl p-8 text-left" data-id="element-101">
            <p className="text-lg leading-relaxed text-teal-50 font-medium" data-id="element-102">
              Cuando el sistema parasimpático toma el control, el cuerpo libera
              automáticamente la retención de líquidos, baja la inflamación
              abdominal y permite que la grasa acumulada por estrés sea
              utilizada como energía.
            </p>
          </div>
        </div>
      </section>

      {/* 5. WHAT YOU GET */}
      <section className="py-20 bg-[#0B1120]" data-id="element-103">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-104">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-16" data-id="element-105">
            Lo que recibes hoy con el Protocolo
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-106">
            {[{
            title: 'Guía Maestra Protocolo',
            desc: 'El paso a paso clínico para hackear tu sistema nervioso.',
            icon: <ShieldCheck className="w-8 h-8 text-amber-500" data-id="element-107" />
          }, {
            title: 'Plan de Acción 14 Días',
            desc: 'Tu calendario diario de comidas, hidratación y pausas de rescate.',
            icon: <Activity className="w-8 h-8 text-amber-500" data-id="element-108" />
          }, {
            title: 'Video-Guía Vagal',
            desc: 'Micro-movimientos de 3 minutos para apagar la ansiedad al instante.',
            icon: <Zap className="w-8 h-8 text-amber-500" data-id="element-109" />
          }, {
            title: 'Nutrición de Rescate',
            desc: 'Los alimentos exactos que calman tu respuesta hormonal.',
            icon: <Battery className="w-8 h-8 text-amber-500" data-id="element-110" />
          }, {
            title: 'Higiene Lumínica',
            desc: 'Cómo configurar tu entorno para dormir profundo hoy mismo.',
            icon: <Moon className="w-8 h-8 text-amber-500" data-id="element-111" />
          }].map((item, i) => <div key={i} className="bg-[#0F172A] border border-slate-800 rounded-2xl p-8 hover:border-amber-500/30 transition-colors" data-id="element-112">
                <div className="bg-[#111827] w-16 h-16 rounded-xl flex items-center justify-center mb-6 border border-slate-800" data-id="element-113">
                  {item.icon}
                </div>
                <h3 className="text-xl font-bold text-white mb-3" data-id="element-114">
                  {item.title}
                </h3>
                <p className="text-slate-400 leading-relaxed" data-id="element-115">{item.desc}</p>
              </div>)}
          </div>
        </div>
      </section>

      {/* 6. WHO THIS IS FOR / NOT FOR */}
      <section className="py-20 bg-[#111827] border-y border-slate-800/50" data-id="element-116">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-117">
          <div className="grid md:grid-cols-2 gap-12" data-id="element-118">
            {/* For */}
            <div className="space-y-6" data-id="element-119">
              <h3 className="text-2xl font-bold text-white flex items-center gap-3 mb-8" data-id="element-120">
                <CheckCircle2 className="w-8 h-8 text-green-500" data-id="element-121" />
                Este protocolo ES para ti si:
              </h3>
              <ul className="space-y-4" data-id="element-122">
                {['Profesionales con altos niveles de responsabilidad y poco tiempo para cocinar.', "Personas que sienten que 'se inflan' conforme avanza el día.", 'Quienes sufren de insomnio o se despiertan a las 3 AM con la mente acelerada.', 'Hombres y mujeres que han probado dietas y ejercicio sin ver cambios en su abdomen.'].map((text, i) => <li key={i} className="flex items-start gap-3" data-id="element-123">
                    <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" data-id="element-124" />
                    <span className="text-lg text-slate-300 leading-relaxed" data-id="element-125">
                      {text}
                    </span>
                  </li>)}
              </ul>
            </div>

            {/* Not For */}
            <div className="space-y-6 bg-[#0B1120] p-8 rounded-2xl border border-slate-800" data-id="element-126">
              <h3 className="text-2xl font-bold text-white flex items-center gap-3 mb-8" data-id="element-127">
                <XCircle className="w-8 h-8 text-red-500" data-id="element-128" />
                NO es para ti si:
              </h3>
              <ul className="space-y-4" data-id="element-129">
                {["Buscas una 'pastilla mágica' sin cambiar tus hábitos matutinos.", 'No estás dispuesto a soltar el celular 15 minutos antes de dormir.', 'Eres un deportista de alto rendimiento que no tiene síntomas de agotamiento o estrés.'].map((text, i) => <li key={i} className="flex items-start gap-3" data-id="element-130">
                    <XCircle className="w-6 h-6 text-red-500/70 flex-shrink-0 mt-0.5" data-id="element-131" />
                    <span className="text-lg text-slate-400 leading-relaxed" data-id="element-132">
                      {text}
                    </span>
                  </li>)}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* 7. HOW IT WORKS */}
      <section className="py-20 bg-[#0F172A]" data-id="element-133">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-134">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-16" data-id="element-135">
            Tu Ruta de 14 Días
          </h2>

          <div className="space-y-8 relative before:absolute before:inset-0 before:ml-5 before:-translate-x-px md:before:mx-auto md:before:translate-x-0 before:h-full before:w-0.5 before:bg-gradient-to-b before:from-teal-500 before:via-amber-500 before:to-slate-800" data-id="element-136">
            {[{
            step: '1',
            title: 'Fase de Descompresión',
            desc: 'Aplicas técnicas físicas para bajar el cortisol base de inmediato.'
          }, {
            step: '2',
            title: 'Reajuste Metabólico',
            desc: 'Introduces nutrientes específicos que reparan tus glándulas adrenales.'
          }, {
            step: '3',
            title: 'Consolidación',
            desc: 'Estableces un ritmo circadiano de hierro para que la energía sea constante todo el día.'
          }].map((item, i) => <div key={i} className="relative flex items-center justify-between md:justify-normal md:odd:flex-row-reverse group is-active" data-id="element-137">
                <div className="flex items-center justify-center w-10 h-10 rounded-full border-4 border-[#0F172A] bg-teal-500 text-slate-900 font-bold shadow shrink-0 md:order-1 md:group-odd:-translate-x-1/2 md:group-even:translate-x-1/2 z-10" data-id="element-138">
                  {item.step}
                </div>
                <div className="w-[calc(100%-4rem)] md:w-[calc(50%-2.5rem)] bg-[#111827] p-6 rounded-xl border border-slate-800 shadow-xl" data-id="element-139">
                  <h3 className="font-bold text-xl text-white mb-2" data-id="element-140">
                    {item.title}
                  </h3>
                  <p className="text-slate-400 leading-relaxed" data-id="element-141">{item.desc}</p>
                </div>
              </div>)}
          </div>
        </div>
      </section>

      {/* 8. PROOF */}
      <section className="py-20 bg-[#0B1120] border-y border-slate-800/50" data-id="element-142">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-143">
          <div className="inline-block mb-6" data-id="element-144">
            <span className="bg-amber-500/10 text-amber-400 border border-amber-500/20 px-4 py-1.5 rounded-full text-sm font-bold uppercase tracking-wider" data-id="element-145">
              Resultados Clínicos
            </span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-8" data-id="element-146">
            La Transformación del Día 15
          </h2>
          <p className="text-xl leading-relaxed text-slate-300 bg-[#0F172A] p-8 rounded-2xl border border-slate-800" data-id="element-147">
            Este no es un plan para meses. Al restaurar el tono vagal, la
            mayoría de los usuarios reportan una reducción de{' '}
            <strong className="text-white" data-id="element-148">
              2 a 4 centímetros de circunferencia abdominal en la primera semana
            </strong>
            , no por pérdida de músculo, sino por el cese de la inflamación
            sistémica.
          </p>
        </div>
      </section>

      {/* 9. FAQ */}
      <section className="py-20 bg-[#111827]" data-id="element-149">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-150">
          <h2 className="text-3xl md:text-4xl font-bold text-center text-white mb-12" data-id="element-151">
            Preguntas Frecuentes
          </h2>
          <FAQAccordion data-id="element-152" />
        </div>
      </section>

      {/* 10. OFFER & FINAL CTA */}
      <section id="checkout" className="py-24 bg-[#0B1120] relative overflow-hidden" data-id="element-153">
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-amber-900/20 blur-[120px] rounded-full pointer-events-none" data-id="element-154"></div>

        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10" data-id="element-155">
          <h2 className="text-4xl md:text-5xl font-extrabold text-white mb-6" data-id="element-156">
            Recupera el control de tu cuerpo hoy.
          </h2>
          <p className="text-xl text-slate-300 mb-12 max-w-2xl mx-auto" data-id="element-157">
            No dejes que el agotamiento se convierta en una enfermedad crónica.
            Obtén el sistema completo diseñado para el mercado hispano con una
            inversión única.
          </p>

          <div className="bg-gradient-to-b from-[#0F172A] to-[#111827] border border-slate-700 rounded-3xl p-8 md:p-12 shadow-2xl max-w-2xl mx-auto relative overflow-hidden" data-id="element-158">
            <div className="absolute top-0 inset-x-0 h-1 bg-gradient-to-r from-teal-500 via-amber-500 to-teal-500" data-id="element-159"></div>

            <div className="mb-8" data-id="element-160">
              <p className="text-teal-400 font-semibold uppercase tracking-wider mb-2" data-id="element-161">
                Precio Especial de Lanzamiento
              </p>
              <div className="flex items-center justify-center gap-3" data-id="element-162">
                <span className="text-3xl text-slate-500 line-through font-bold" data-id="element-163">
                  $27
                </span>
                <span className="text-6xl font-extrabold text-amber-400" data-id="element-164">
                  $6.39
                </span>
                <span className="text-2xl text-slate-400 font-medium mt-4" data-id="element-165">
                  USD
                </span>
              </div>
            </div>

            <div className="space-y-6" data-id="element-166">
              <CTAButton className="w-full py-6 text-xl" data-id="element-167" />

              <div className="flex flex-col items-center gap-4 pt-4 border-t border-slate-800" data-id="element-168">
                <div className="flex items-center gap-2 text-slate-400" data-id="element-169">
                  <ShieldCheck className="w-5 h-5 text-green-500" data-id="element-170" />
                  <span data-id="element-171">Acceso inmediato y seguro a través de Hotmart.</span>
                </div>
                <div className="flex items-center gap-2 text-slate-400" data-id="element-172">
                  <CheckCircle2 className="w-5 h-5 text-green-500" data-id="element-173" />
                  <span data-id="element-174">7 días de garantía de satisfacción.</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <LegalFooter data-id="element-175" />
    </div>;
}